import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import Gender from "../../enums/gender.enum"
import { useState, useContext, useRef, useEffect } from "react"
import { Row, Col, Typography } from "antd"
import { UserContext } from "../../contexts/user.context"
import ProductTypeCode from "../../enums/productTypeCode.enum"
import { HeartFlowConfirm } from "./heartFlowConfirm.component.js"
import { listProductTypes } from '../../services/productType.service'
import { completeFlow } from "../../services/flow.service.js"
import FlowType from "../../enums/flowType.enum"
import ProductHelper from "../../helpers/product.helper"
import PayTo from "../../enums/payTo.enum"
import UserHelper from "../../helpers/user.helper"
import { Button } from "antd"
import Panels  from "../panels/panels.component"
import FieldType from "../../enums/fieldType.enum"
import MedicalCondition from "../../enums/medicalCondition.enum"
import Tracker from "../../enums/tracker.enum"
import PatientHelper from "../../helpers/patient.helper"
import Disqualification from "../../enums/disqualification.enum"
import { CheckOutlined } from "@ant-design/icons"

const { Text } = Typography
export const Step = {
  INTRO: 'intro',
  GENDER: 'gender',
  DOB: 'dob',
  PREGNANCY: 'pregnancy',
  CURRENT_MEDICAL_CONDITIONS: 'current-medical-conditions',
  PREVIOUS_DIAGNOSES: 'previous-diagnoses',
  HEART_ATTACK_DATE: 'heart-attack-date',
  HEART_PROCEDURES: 'heart-procedures',
  CHEST_PAIN: 'chest-pain',
  CHEST_PAIN_DETAILS: 'chest-pain-details',
  PAST_SMOKING: 'past-smoking',
  CURRENT_SMOKING: 'current-smoking',

  CONTRAST_DYE_ALLERGY: 'contrast-dye-allergy',
  HAS_MEDICATIONS: 'has-medications',
  MEDICATIONS: 'medications', 
  IODINE_TREATMENT: 'iodine-treatment', // TO DO
  HAS_SUPPLEMENTS: 'has-supplements', 
  SUPPLEMENTS: 'supplements',
  HOLDBREATH: 'holdbreath',
  FAMILY_HISTORY: 'family-history',

  QUALIFIED: 'qualified',
  DISQUALIFIED: 'disqualified',

  LOCATION: 'location',
  FACILITY: 'facility',
  CONTACT: 'contact',
  PAY: 'pay',
  ADDITIONAL_INFO: 'additional-info',
  PASSWORD: 'password',
  CONFIRM: 'confirm',
}

const currentMedicalConditions = [
  MedicalCondition.HIGH_BLOOD_PRESSURE,
  MedicalCondition.HIGH_CHOLESTEROL,
  MedicalCondition.DIABETES,
  MedicalCondition.COPD,
  MedicalCondition.ASTHMA,
  MedicalCondition.CHRONIC_KIDNEY_DISEASE,
  MedicalCondition.HYPOTHYROIDISM,
  MedicalCondition.ARRHYTHMIA,
  MedicalCondition.TACHYCARDIA,
]

const previousDiagnoses = [
  MedicalCondition.HEART_ATTACK,
  MedicalCondition.HEART_FAILURE,
  MedicalCondition.HEART_BLOCK,
]

const heartProcedures = [
  MedicalCondition.ABNORMAL_STRESS_TEST,
  MedicalCondition.BYPASS_SURGERY,
  MedicalCondition.HEART_CATHER,
  MedicalCondition.POSITIVE_CAC,
]

const familyHistoryConditions = [
  MedicalCondition.HEART_ATTACK,
  MedicalCondition.HEART_DISEASE,
  MedicalCondition.PERIPHERAL_ARTERY_DISEASE,
  MedicalCondition.STROKE,
]


export const HeartFlowFlow = () => {
  const { instalabMembership, currentUser } = useContext(UserContext)
  const [flow, setFlow] = useState()
  const [skeleton, setSkeleton] = useState()
  const [productTypes, setProductTypes] = useState()
  const [hasPass, setHasPass] = useState()
  const hasCompletedFlowRef = useRef(false)
  const [selectedMenuItem, setSelectedMenuItem] = useState(null)
  const [initialUser, setInitialUser] = useState()
  const [disqualifications, setDisqualifications] = useState([])
  useEffect(() => {
    fetchSkeleton()
  }, [productTypes, hasPass, initialUser, instalabMembership, flow, currentUser, selectedMenuItem])

  useEffect(() => {
    fetchProductTypes()
  }, [])
  
  const fetchProductTypes = async () => {
    const response = await listProductTypes({
      filter: {
        code: {
          $in: [
            ProductTypeCode.HEARTFLOW,
            ProductTypeCode.HEARTFLOW_CONCIERGE_FEE,
          ]
        }
      }
    })
    setProductTypes(response)
  }

  useEffect(() => {
    if (flow?.facility && flow.facility.menu) {
      const product = flow.facility.menu.find(f => f.productType === getProductType(ProductTypeCode.HEARTFLOW)?._id);
      setSelectedMenuItem(product);
    
    }
  }, [flow, productTypes]);



  const getProductType = (code) => {
    return productTypes?.find(productType => productType.code === code)
  }

 
  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser || !productTypes) return

    const totalCost = ProductHelper.getTotalCostV2({
      productTypes: [getProductType(ProductTypeCode.HEARTFLOW), getProductType(ProductTypeCode.HEARTFLOW_CONCIERGE_FEE)],
      instalabMembership,
      currentUser,  
      priceOverride: selectedMenuItem?.payTo === PayTo.INSTALAB ? [{
        code: ProductTypeCode.HEARTFLOW,
        price: selectedMenuItem?.price
      }] : null
    })

    const hasCredit = UserHelper.hasCredit(currentUser, ProductTypeCode.HEARTFLOW)

    setSkeleton({
      [Step.INTRO]: {
        type: StepType.STATIC,
        title: `First, let's see if CCTA with HeartFlow is right for you.`,
        description: `We’ll ask just a few quick questions about you. Your answers will help us determine whether a Coronary CT Angiogram is a good fit for you right now.`,
        nextStep: Step.GENDER,
        buttonText: "Let's get started!",
      },

      [Step.GENDER]: {
        type: StepType.SINGLE_SELECT,
        onNextStep: async (patient) => {
          if (patient?.gender === Gender.FEMALE) {
            return Step.PREGNANCY
          } else {
            return Step.DOB
          }
        },
        buildUser: true,
        title: `What's your biological sex?`,
        field: {
          name: 'gender',
          options: [{
            label: 'Male',
            value: Gender.MALE,
          }, {
            label: 'Female',
            value: Gender.FEMALE,
          }]
        },
        skipIf: () => {
          return initialUser?.gender
        }
      },
      [Step.PREGNANCY]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.DOB,
        buildUser: true,
        title: `Are you currently pregnant, or might you be pregnant?`,
        field: {
          name: 'isPregnant',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
        skipIf: () => {
          return initialUser?.gender === Gender.MALE
        }
      },

      [Step.DOB]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CURRENT_MEDICAL_CONDITIONS,
        buildUser: true,
        title: `What is your date of birth?`,
        description: `Enter in the format of MM/DD/YYYY.`,
        field: {
          name: 'dob',
          placeholder: 'MM/DD/YYYY',
          date: true,
          inputMode: 'numeric',
          required: true,
        },
        skipIf: () => {
          return initialUser?.dob
        }
      },

      [Step.CURRENT_MEDICAL_CONDITIONS]: {
        type: StepType.MULTIPLE_SELECT,
        buildUser: true,
        title: `Do you currently have any of the following conditions?`,
        description: 'Select all that apply.',
        field: {
          name: 'currentMedicalConditions',
          options: [...Object.values(MedicalCondition)
                          .filter(value => currentMedicalConditions.includes(value))
                          .map(value => ({label: value, value: value}))
                          .sort((a, b) => a.label.localeCompare(b.label))]
        },
        nextStep: Step.PREVIOUS_DIAGNOSES
      },

      [Step.PREVIOUS_DIAGNOSES]: {
        type: StepType.MULTIPLE_SELECT,
        buildUser: true,
        onNextStep: (user) => {
          if (user?.allMedicalConditions?.includes(MedicalCondition.HEART_ATTACK)) {
            return Step.HEART_ATTACK_DATE
          }
          else {
            return Step.HEART_PROCEDURES
          }
        },
        title: `Have you ever been diagnosed with any of the following conditions?`,
        description: 'Select all that apply.',
        field: {
          name: 'allMedicalConditions',
          options: [...Object.values(MedicalCondition)
                          .filter(value => previousDiagnoses.includes(value))
                          .map(value => ({label: value, value: value}))
                          .sort((a, b) => a.label.localeCompare(b.label))]
        },
      },

      [Step.HEART_ATTACK_DATE]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.HEART_PROCEDURES,
        buildUser: true,
        title: `What was the date of your heart attack?`,
        description: `Enter in the format of MM/DD/YYYY. If more than one, enter the most recent.`,
        field: {
          name: 'heartAttackDate',
          placeholder: 'MM/DD/YYYY',
          date: true,
          inputMode: 'numeric',
          required: true,
        },
      },

      [Step.HEART_PROCEDURES]: {
        type: StepType.MULTIPLE_SELECT,
        buildUser: true,
        nextStep: Step.CHEST_PAIN,
        title: `Have you had any of the following heart-related test results or procedures?`,
        description: 'Select all that apply.',
        field: {
          name: 'heartProcedures',
          options: [...Object.values(MedicalCondition)
                          .filter(value => heartProcedures.includes(value))
                          .map(value => ({label: value, value: value}))
                          .sort((a, b) => a.label.localeCompare(b.label))]
        },
      },

      [Step.CHEST_PAIN]: {
        type: StepType.SINGLE_SELECT,
        buildUser: true,
        onNextStep: (user) => {
          if (user?.hasChestPain) {
            return Step.CHEST_PAIN_DETAILS
          }
          else {
            return Step.PAST_SMOKING
          }
        },
        title: `Do you currently experience any chest pain, pressure, or tightness?`,
        field: {
          name: 'hasChestPain',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },

      [Step.CHEST_PAIN_DETAILS]: {
        type: StepType.MULTIPLE_SELECT,
        buildUser: true,
        title: `How does the chest pain present itself?`,
        description: 'Select all that apply.',
        nextStep: Step.PAST_SMOKING,
        field: {
          name: 'chestPainDetails',
          options: [
            {
              label: 'The pain spreads to arm, jaw, neck or back',
              value: 'arm-jaw-neck-back',
            },
            {
              label: 'You feel shortness of breath with little effort',
              value: 'shortness-of-breath-with-little-effort',
            },
            {
              label: 'You feel nausea or cold sweat with the above feelings',
              value: 'nausea-cold-sweat',
            },
            {
              label: 'Symptoms started suddenly within the past 24 hours',
              value: 'symptoms-started-suddenly',
            },
            {
              label: 'Symptoms occur mostly with activity but ease at rest',
              value: 'symptoms-occur-mostly-with-activity',
            },
          ]
        }
      },

      [Step.PAST_SMOKING]: {
        type: StepType.SINGLE_SELECT,
        buildUser: true,
        onNextStep: async (patient) => {
          if (patient?.hasPreviousSmoking) {
            return Step.CURRENT_SMOKING
          } else {
            return Step.CONTRAST_DYE_ALLERGY
          }
        },
        title: `Have you ever smoked or vaped nicotine regularly?`,
        field: {
          name: 'hasPreviousSmoking',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        }
      },
      [Step.CURRENT_SMOKING]: {
        type: StepType.SINGLE_SELECT,
        buildUser: true,
        nextStep: Step.CONTRAST_DYE_ALLERGY,
        title: `Do you currently smoke or vape nicotine regularly?`,
        field: {
          name: 'hasSmoking',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        }
      },
      [Step.CONTRAST_DYE_ALLERGY]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.IODINE_TREATMENT,
        buildUser: true,
        title: 'Have you ever had a serious reaction to iodine-based contrast dye?',
        description: 'This will be used to help visualize the coronary arteries.',
        field: {
          name: "hasContrastDyeAllergy",
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },

      [Step.IODINE_TREATMENT]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.HOLDBREATH,
        buildUser: true,
        title: `Have you had any radioactive‑iodine treatment in the past year?`,
        field: {  
          name: 'hasRecentIodineTreatment',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',  
            value: false,
          }]
        },
      },
      [Step.HOLDBREATH]: {
        type: StepType.SINGLE_SELECT,
        buildUser: true,
        nextStep: Step.FAMILY_HISTORY,
        title: `Can you lie still and hold your breath for more than 10 seconds?`,
        field: {
          name: 'holdBreath',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },
      [Step.FAMILY_HISTORY]: {
        type: StepType.MULTIPLE_SELECT,
        buildUser: true,
        title: <>Which medical conditions has your immediate family been diagnosed with <u>before age 60</u>?</>,
        description: 'This includes any grandparents, parents, or siblings. Select all that apply.',
        onNextStep: (patient) => {
          
          const result = PatientHelper.cctaQualified(patient)
          const { qualified, disqualifications } = result

          if (qualified) {
            return Step.QUALIFIED
          }
          else {
            setDisqualifications(disqualifications)
            return Step.DISQUALIFIED
          }
        },
        field: {
          name: 'familyHistory',
          options: Object.values(MedicalCondition)
                          .filter(value => familyHistoryConditions.includes(value))
                          .map(value => ({label: value, value: value}))
                          .sort((a, b) => a.label.localeCompare(b.label))
        },
      },
      
      [Step.QUALIFIED]: {
        type: StepType.STATIC,
        title: `You’re eligible for a Coronary CT Angiogram!`,
        description: () => {
          const benefits = [
            'Coronary CT angiogram to visualize your heart’s arteries in detail',
            'HeartFlow analysis for precise insights into blood flow, plaque build up and potential blockages',
            'Follow-up consultation with an Instalab physician to review your results and guide your next steps'
          ]
          return <>Next, let's set up your appointment to get this done. With Instalab, you’ll receive:
            <div className="benefits-list">
              {benefits.map((benefit, index) => (
                <div key={`benefit-${index}`} className="benefit-item">
                  <div className="benefit-col-1">
                    <CheckOutlined />
                  </div>
                  <div className="benefit-col-2">
                    <Text className="benefit-item-text">{benefit}</Text>
                  </div>
              </div>
              ))}
            </div>
          </>
        },
        buttonText: 'Continue',
        nextStep: Step.LOCATION,
      },

      [Step.DISQUALIFIED]: {
        type: StepType.STATIC,
        title: `Apologies, we don't recommend a CCTA at this time.`,
        description: () => {

          let text = ''
          switch (disqualifications?.[0]) {
            case Disqualification.PREGNANT:
              text = 'CCTA involves exposure to radiation, which can pose risks to your developing baby. For this reason, it is recommended to wait until after delivery to undergo this test.'
              break
            case Disqualification.RECENT_HEART_ATTACK:
              text = 'After a heart attack, your heart is in a delicate state. A CCTA might not provide the immediate information needed for treatment. In such cases, a cardiac catheterization (coronary angiogram) is preferred, as it allows real-time visualization and potential treatment of blockages.'
              break
            case Disqualification.HEART_BLOCK:
            case Disqualification.HEART_FAILURE:
              text = 'CCTA often requires beta blockers to slow the heart rate, which can be problematic in heart failure or heart block conditions. A coronary calcium scan is a safer alternative that doesn’t require medication or contrast dye. If more detailed imaging is necessary, a stress echocardiogram or nuclear stress test can be considered.'
              break
            case Disqualification.ASTHMA:
              text = 'Asthma can make breath-holding difficult during CCTA. Beta blockers used for the test may also trigger asthma symptoms. A coronary calcium scan is a safer initial test, and if more information is needed, a stress echocardiogram or nuclear stress test can be utilized without the risks associated with CCTA.'
              break
            case Disqualification.COPD:
              text = 'CCTA requires lying flat and holding your breath, which can be challenging with COPD. Additionally, beta blockers used to slow the heart rate for the test can exacerbate breathing issues. A better alternative is a coronary calcium scan, which is quick, uses minimal radiation, and doesn’t require breath-holding or contrast dye. If further detail is needed, a stress echocardiogram or nuclear stress test can be considered.'
              break
            case Disqualification.ARRHYTHMIA:
              text = 'CCTA requires taking images between heartbeats. An irregular heart rhythm can make this difficult or impossible. In such cases, a nuclear stress test or stress echocardiogram can provide reliable information about your heart’s function without relying on a steady heart rhythm.'
              break
            case Disqualification.TACHYCARDIA:
              text = 'CCTA relies on a slow, regular heartbeat to produce the best images. Poor-quality images can result from a quick heartbeat.  A better option is a nuclear stress test or stress echocardiogram, which lets us check how your heart performs under stress without needing your heart rate to be low.'
              break
            case Disqualification.CHRONIC_KIDNEY_DISEASE:
              text = 'Because your kidneys aren’t filtering well, the contrast dye used in CCTA could put more strain on them — or even make your kidney function worse. A coronary calcium scan is a safer initial test, and if more information is needed, a stress echocardiogram or nuclear stress test can be utilized without the risks associated with CCTA.'
              break
            case Disqualification.HOLD_BREATH:
              text = 'If you’re unable to hold your breath for more than 10 seconds, a CCTA won’t give us clear results. A better alternative is a coronary calcium scan, which is quick, uses minimal radiation, and doesn’t require breath-holding or contrast dye. If further detail is needed, a stress echocardiogram or nuclear stress test can be considered.'
              break
            case Disqualification.CONTRAST_DYE_ALLERGY:
              text = 'A history of allergic reactions to contrast dye makes CCTA risky. Even with premedication, it’s often safer to choose tests that don’t involve contrast dye.  A coronary calcium scan is a safer initial test, and if more information is needed, a stress echocardiogram or nuclear stress test can be utilized without the risks associated with CCTA.'
              break
            case Disqualification.RECENT_IODINE_TREATMENT:
              text = 'Since you’ve had recent iodine treatment, adding more iodine from the contrast dye in CCTA might interfere with your treatment or increase your risk of a reaction. A coronary calcium scan is a safer initial test, and if more information is needed, a stress echocardiogram or nuclear stress test can be utilized without the risks associated with CCTA.'
              break
            case Disqualification.YOUNG_NO_RISK_FACTORS:
              text = 'You’re doing the right thing by being proactive - but since you’re relatively young and have no risk factors for heart disease, we don’t recommend CCTA. This imaging modality provides the most useful information for men  ≥45 y/o or women ≥55 y/o. For now, tracking risk factors like ApoB, LDL, and hs-CRP is a great way to monitor your heart health. If you want imaging, a coronary calcium scan is a better option.'
              break
            default:
              text = null
              break;
          }

          return <>
            {text ? <p>{text}.</p> : <p>Based on your answers, we do not recommend a CCTA at this time.</p>}
            <p>Please contact us if you disagree or have any questions. We're happy to consider situations on a case-by-case basis.</p>
          </>
        },
        buttonText: 'Get Help',
        path: '/help',
        enterStyle: { display: 'none' },
        buttonStyle: { display: 'none' },
        content: <div>
          <Button
            className="question-btn"
            onClick={() => window.FrontChat("show")}
          >
            I have a question?
          </Button>

          <Panels excludeCodes={[ProductTypeCode.CLEERY, ProductTypeCode.HEARTFLOW]}/>

        </div>

      },

      
      [Step.LOCATION]: {
        type: StepType.LOCATION,
        buildUser: true,
        buildProduct:true,
        nextStep: Step.FACILITY,
        title: `What's your current address?`,
        description: `We'll match you to nearby imaging centers for your CCTA with HeartFlow.`,
        field: {
          name: 'location'
        },
      },
      [Step.FACILITY]: {
        type: StepType.FACILITY,
        nextStep: Step.CONTACT,
        buildFlow: true,
        productTypeCode: [ProductTypeCode.HEARTFLOW],
        addendumProductTypeCode: [ProductTypeCode.HEARTFLOW_CONCIERGE_FEE],
        showPrice: !hasCredit,
        title: `Choose your preferred location.`,
        description: `We partner with top-rated imaging centers. Select the one that works best for you.`,
        field: {
          name: 'facility',
          required: true,
        },
      },


      [Step.CONTACT]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PAY,
        buildUser: true,
        title: `How can we reach you?`,
        description: "We’ll send your appointment confirmation, directions and results here.",
        fields: [
          {
            type: "group",
            label: "Full Name",
            required: true,
            fields: [
              {
                name: 'firstName',
                placeholder: 'First Name',
                label: 'First Name',
                required: true,
                type: 'text',
              },  
              {
                name: 'lastName',
                placeholder: 'Last Name',
                label: 'Last Name',
                required: true,
                type: 'text',
              },
            ]
          },
          {
          name: 'email',
          label: 'Email Address',
          required: true,
          type: 'email',
          unique: true,
        }, {
          name: 'phone',
          label: 'Phone Number',
          required: true,
          type: 'phone',
        }],
        skipIf: () => {
          return hasPass && (initialUser?.email && initialUser?.phone && initialUser?.firstName && initialUser?.lastName)
        }
      },


      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.ADDITIONAL_INFO,
        addProduct: true,
        priceOverride: () => {
          return (selectedMenuItem?.payTo === PayTo.INSTALAB)? [{
            code: ProductTypeCode.HEARTFLOW,
            price: selectedMenuItem?.price
          }] : null
        },
        productType: [ProductTypeCode.HEARTFLOW, ProductTypeCode.HEARTFLOW_CONCIERGE_FEE],
        title: () => {
          
          if (selectedMenuItem?.payTo === PayTo.INSTALAB) {
            return  <>Pay <span className="true-price">${totalCost}</span> for your <span className="product-name">{getProductType(ProductTypeCode.HEARTFLOW).title}</span>.</>
          }
          else {
            return  <>Pay <span className="true-price">${totalCost}</span> for the <span className="product-name">{getProductType(ProductTypeCode.HEARTFLOW_CONCIERGE_FEE).title.toLowerCase()}</span> to get started.</>
          }
        },
        description: () => {
          if (selectedMenuItem?.payTo === PayTo.INSTALAB) {  
            return <>Includes everything: physician order, appointment scheduling, the scan, HeartFlow report, and a follow-up with an Instalab physician.</>
          }
          else {
            return <>This fee covers your physician order, appointment scheduling, HeartFlow report, and a follow-up with an Instalab physician. {!hasCredit && <p>Note: The remaining {selectedMenuItem?.price && ` $${selectedMenuItem?.price}`} will be paid directly to {flow?.facility ? flow?.facility.name : 'the facility'} at time of your appointment.</p>} </>
          }
        },
        skipIf: () => {
          return (totalCost === 0) || hasCredit
        },
      },

      [Step.ADDITIONAL_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PASSWORD,
        buildUser: true,
        buttonText: 'Finish',
        title: `One last step to finalize your order.`,
        description: `We just need a few last details for your physician order and to ensure scan safety.`,
        fields: [

          {
            type: FieldType.GROUP,     
            required: true,
            label: <span className="highlight-label">What is your height and weight?</span>,
            fields: [
              {
                name: 'height',
                placeholder: 'Enter your height ...',
                required: true,
                type: 'text',
                unit: 'inches',
              },  
              {
                name: 'weight',
                placeholder: 'Enter your weight ...',
                required: true,
                type: 'text',
                unit: 'lbs',
              },
            ]
          },
          {
            type: FieldType.SELECT,
            name: 'hasPacemaker',
            label: <span className="highlight-label">Do you have a pacemaker or a heart rhythm device?</span>,
            required: true,
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              }
            ]
          },

          {
            type: FieldType.SELECT,
            name: 'hasRHROver60',
            label: <span className="highlight-label">Is your resting heart rate usually over 60 bpm?</span>,
            required: true,
            options: [
              {
                label: 'No, it\'s under 60 bpm.',
                value: 'no',
              },
              {
                label: 'Yes, it\'s over 60 bpm.',
                value: 'yes',
              },
              {
                label: 'I don\'t know',
                value: 'unknown',
              }

          ]
          },
          {
            type: FieldType.TEXTAREA,
            name: 'freeformAllergies',
            label: <span className="highlight-label">List any allergies you currently have.</span>,
          },   
          {
            type: FieldType.TEXTAREA,
            name: 'freeformMedications',
            label: <span className="highlight-label">List any medications you currently take.</span>,
          },   
          {
            type: FieldType.TEXTAREA,
            name: 'freeformSupplements',
            label: <span className="highlight-label">List any vitamins, supplements, or over-the-counter medications you currently take.</span>,
          },     

        ],
      },

      

      [Step.PASSWORD]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CONFIRM,
        buildUser: true,
        title: `Create a password for your Instalab account.`,
        description: `This lets you securely access your results, manage appointments, and track your health over time.`,
        field: {
          name: 'password',
          placeholder: 'Enter your password',
          required: true,
          password: true,
        },
        skipIf: () => {
          return hasPass
        },
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },

      [Step.CONFIRM]: {
        type: StepType.STATIC,
        path: `/flow/${FlowType.INTAKE}`,
        title: `All set! `,
        description: <>We'll email you in 1 business day to schedule your appointment. Please hit 'Continue' to tell more about yourself so we can personalize your results report.</>,
        buttonText: 'Continue',

        content: <HeartFlowConfirm flow={flow} />,
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },
    })

  
  }
  

  return ( <>
    <Flow 
      skeleton={skeleton} 
      flow={flow} 
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
      startIndex={0}
      productTypeCode={[ProductTypeCode.HEARTFLOW, ProductTypeCode.CONCIERGE_FEE]}
    />
    </>
  )
}